import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isSupabaseConfigured, requireSupabase } from '../lib/supabase';
import { redeemPendingRegistrationPromo } from '../services/promoCodesService';

const AuthContext = createContext(null);

export const shouldHydrateAuthEvent = (event, nextSession, hydratedUserId) => {
  return event !== 'TOKEN_REFRESHED' || nextSession?.user?.id !== hydratedUserId;
};

export const toAppUser = (authUser, profile) => {
  return {
    id: authUser.id,
    sub: authUser.id,
    oid: authUser.id,
    email: authUser.email || '',
    nombre: profile?.full_name || authUser.user_metadata?.full_name || authUser.email || 'Usuario',
    rol: profile?.system_role || 'user',
    professionalRole: profile?.default_professional_role || null,
    professionalRoles: profile?.default_professional_role ? [profile.default_professional_role] : [],
  };
};

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorizationError, setAuthorizationError] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const hydrationSequence = useRef(0);
  const hydratedUserId = useRef(null);
  const activeSessionUserId = useRef(null);
  const pendingPromoAttemptedUserId = useRef(null);

  const loadAuthorization = useCallback(async (nextSession, sequence) => {
    const client = requireSupabase();
    const [{ data: profileData, error: profileError }, permissionResult] =
      await Promise.all([
        client.from('profiles').select('*').eq('id', nextSession.user.id).single(),
        client.rpc('get_my_permissions'),
      ]);

    if (profileError) throw profileError;
    if (hydrationSequence.current !== sequence) return;

    if (permissionResult.error) throw permissionResult.error;
    setProfile(profileData);
    hydratedUserId.current = nextSession.user.id;
    setPermissions((permissionResult.data || []).map(({ permission_code }) => permission_code));
    setPermissionError('');
    setLoading(false);
  }, []);

  const hydrate = useCallback(async (nextSession) => {
    const sequence = hydrationSequence.current + 1;
    const nextUserId = nextSession?.user?.id || null;
    const userChanged = activeSessionUserId.current !== nextUserId;
    activeSessionUserId.current = nextUserId;
    hydrationSequence.current = sequence;
    setSession(nextSession);
    const refreshingSameUser = nextUserId && !userChanged && hydratedUserId.current === nextUserId;
    if (!refreshingSameUser) {
      setProfile(null);
      setPermissions([]);
      setLoading(Boolean(nextSession?.user));
    }

    if (!nextSession?.user) {
      hydratedUserId.current = null;
      pendingPromoAttemptedUserId.current = null;
      setAuthorizationError('');
      setPermissionError('');
      setLoading(false);
      return;
    }

    if (userChanged) {
      setAuthorizationError('');
      setPermissionError('');
    }
    if (pendingPromoAttemptedUserId.current !== nextUserId) {
      pendingPromoAttemptedUserId.current = nextUserId;
      try {
        await redeemPendingRegistrationPromo(nextSession);
        setAuthorizationError('');
      } catch (pendingPromoError) {
        setAuthorizationError(
          pendingPromoError.message || 'No se pudo aplicar el código promocional pendiente.'
        );
      }
    }
    try {
      await loadAuthorization(nextSession, sequence);
    } catch (error) {
      if (hydrationSequence.current === sequence) {
        setProfile(null);
        setPermissions([]);
        hydratedUserId.current = null;
        setPermissionError('No se pudo comprobar tu acceso. Reintenta la comprobación; esto no significa que necesites otra licencia.');
        setLoading(false);
      }
      throw error;
    }
  }, [loadAuthorization]);

  const refreshAuthorization = useCallback(async (sessionOverride) => {
    const nextSession = sessionOverride?.user ? sessionOverride : session;
    if (!nextSession?.user) throw new Error('No hay una sesión activa.');
    if (activeSessionUserId.current !== nextSession.user.id) {
      throw new Error('La sesión ha cambiado. Vuelve a realizar la operación.');
    }
    const sequence = hydrationSequence.current + 1;
    hydrationSequence.current = sequence;
    setLoading(true);
    try {
      await loadAuthorization(nextSession, sequence);
    } catch (error) {
      if (hydrationSequence.current === sequence) {
        setProfile(null);
        setPermissions([]);
        hydratedUserId.current = null;
        setPermissionError('No se pudo comprobar tu acceso. Reintenta la comprobación; esto no significa que necesites otra licencia.');
        setLoading(false);
      }
      throw error;
    }
  }, [loadAuthorization, session]);

  const retryPendingRegistrationPromo = useCallback(async () => {
    if (!session?.user) throw new Error('No hay una sesión activa.');
    pendingPromoAttemptedUserId.current = session.user.id;
    try {
      const result = await redeemPendingRegistrationPromo(session);
      await refreshAuthorization(session);
      setAuthorizationError('');
      return result;
    } catch (error) {
      setAuthorizationError(
        error.message || 'No se pudo aplicar el código promocional pendiente.'
      );
      throw error;
    }
  }, [refreshAuthorization, session]);

  const clearAuthorizationError = useCallback(() => {
    setAuthorizationError('');
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return undefined;
    }

    const client = requireSupabase();
    let active = true;
    const initialSequence = hydrationSequence.current;
    client.auth.getSession()
      .then(({ data, error }) => {
        if (!active || hydrationSequence.current !== initialSequence) return;
        if (error) throw error;
        return hydrate(data.session);
      })
      .catch((error) => {
        console.error('No se pudo restaurar la sesión de Supabase:', error);
        if (hydrationSequence.current === 0) setLoading(false);
      });

    const { data: subscription } = client.auth.onAuthStateChange((event, nextSession) => {
      window.setTimeout(() => {
        if (!active) return;
        if (!shouldHydrateAuthEvent(event, nextSession, hydratedUserId.current)) {
          setSession(nextSession);
          return;
        }

        hydrate(nextSession).catch((error) => {
          console.error('No se pudo sincronizar la sesión de Supabase:', error);
        });
      }, 0);
    });

    return () => {
      active = false;
      hydrationSequence.current += 1;
      subscription.subscription.unsubscribe();
    };
  }, [hydrate]);

  const signIn = useCallback(async ({ email, password }) => {
    const { error } = await requireSupabase().auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async ({ email, password, fullName, professionalRole }) => {
    const redirectTo = `${window.location.origin}/login`;
    const { data, error } = await requireSupabase().auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          full_name: fullName,
          professional_role: professionalRole || undefined,
        },
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await requireSupabase().auth.signOut();
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(async (email) => {
    const { error } = await requireSupabase().auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await requireSupabase().auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const updateProfile = useCallback(async ({ fullName, professionalRole }) => {
    if (!session?.user) throw new Error('No hay una sesión activa.');
    const changes = { full_name: fullName };
    if (professionalRole !== undefined) {
      changes.default_professional_role = professionalRole || null;
    }
    const { data, error } = await requireSupabase()
      .from('profiles')
      .update(changes)
      .eq('id', session.user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
  }, [session]);

  const value = useMemo(() => {
    const user = session?.user ? toAppUser(session.user, profile) : null;
    return {
      session,
      user,
      profile,
      permissions,
      loading,
      authorizationError,
      permissionError,
      isAuthenticated: Boolean(session?.user),
      hasPermission: (permission) => permissions.includes(permission) || profile?.system_role === 'admin',
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      updateProfile,
      refreshAuthorization,
      retryPendingRegistrationPromo,
      clearAuthorizationError,
    };
  }, [authorizationError, permissionError, clearAuthorizationError, permissions, profile, session, loading, refreshAuthorization, resetPassword, retryPendingRegistrationPromo, signIn, signOut, signUp, updatePassword, updateProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe utilizarse dentro de AuthProvider.');
  return context;
}
